import { REHYDRATE } from 'redux-persist'
import { TimeLine } from '../generated/graphql'
import { convertDatesToStrings } from '../utils'

/**
 * This is the replay reducer.
 * We keep in local memory information of the timelines items loaded (raw data)
 * and the current state of the node (snapshot).
 */
export interface ReplayState {
  chunks: readonly TimeLineItem[][] // Direct updates on a reducer state can imply a illegal mutation and the hooks will not be affected
  totalItems: number
  totalChunks: number
  nodeId: string
}

// Define TimeLineItem type
export type TimeLineItem = Partial<TimeLine> & {
  chunk: number
  loading: boolean
}

// Define action types
export enum ReplayActionTypes {
  SET_CHUNKS = 'SET_CHUNKS',
  SET_TOTAL_ITEMS = 'SET_TOTAL_ITEMS',
  SET_TOTAL_CHUNKS = 'SET_TOTAL_CHUNKS',
  SET_NODE_ID = 'SET_NODE_ID',
}

// Define the action interfaces

interface SetChunksAction {
  type: ReplayActionTypes.SET_CHUNKS
  payload: TimeLineItem[][]
}

interface SetTotalItemsAction {
  type: ReplayActionTypes.SET_TOTAL_ITEMS
  payload: number
}

interface SetTotalChunksAction {
  type: ReplayActionTypes.SET_TOTAL_CHUNKS
  payload: number
}

interface SetNodeIdAction {
  type: ReplayActionTypes.SET_NODE_ID
  payload: string
}

// Union type for all possible actions
export type ReplayAction =
  | SetChunksAction
  | SetTotalItemsAction
  | SetTotalChunksAction
  | SetNodeIdAction
// Initial state
export const initialReplayState: ReplayState = {
  chunks: [],
  totalItems: 0,
  totalChunks: 0,
  nodeId: '',
}

// Reducer function
export const replayReducer = (
  state: ReplayState = initialReplayState,
  action: ReplayAction | { type: typeof REHYDRATE; payload: ReplayState }
): ReplayState => {
  switch (action.type) {
    case ReplayActionTypes.SET_CHUNKS:
      const parsedPayload = convertDatesToStrings(action.payload)
      return {
        ...state,
        chunks: parsedPayload,
      }

    case ReplayActionTypes.SET_TOTAL_ITEMS:
      return {
        ...state,
        totalItems: action.payload,
      }

    case ReplayActionTypes.SET_TOTAL_CHUNKS:
      return {
        ...state,
        totalChunks: action.payload,
      }

    case ReplayActionTypes.SET_NODE_ID:
      return {
        ...state,
        nodeId: action.payload,
      }

    default:
      return state
  }
}
export const storeSetChunks = (chunks: TimeLineItem[][]): SetChunksAction => {
  return {
    type: ReplayActionTypes.SET_CHUNKS,
    payload: chunks,
  }
}

export const storeSetTotalItems = (
  totalItems: number
): SetTotalItemsAction => ({
  type: ReplayActionTypes.SET_TOTAL_ITEMS,
  payload: totalItems,
})

export const storeSetTotalChunks = (
  totalChunks: number
): SetTotalChunksAction => ({
  type: ReplayActionTypes.SET_TOTAL_CHUNKS,
  payload: totalChunks,
})

export const storeSetNodeId = (nodeId: string): SetNodeIdAction => ({
  type: ReplayActionTypes.SET_NODE_ID,
  payload: nodeId,
})
